import { isEqual } from 'lodash';
import { memo } from 'react';
import styled from 'styled-components';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';

interface Props {
  readonly maxLength: number;
  readonly legs: readonly string[];
}

function LegsDescription({ maxLength, legs }: Props) {
  const areLegsValidForColumnDisplay = legs.every(isValidForDisplay);
  const displayedLegs = legs.slice(0, maxLength);

  const shouldShowEllipsis = (leg: string) => {
    const index = legs.indexOf(leg);
    const isLast = index + 1 === maxLength;
    const showEllipsis = isLast && legs.length > maxLength;

    return showEllipsis;
  };

  /**
   * If there is any leg that does not follow the known pattern,
   * just display each leg directly.
   */
  if (!areLegsValidForColumnDisplay) {
    return (
      <div data-testid="legs-description-lines">
        {displayedLegs.map((leg) => (
          <div key={leg}>{`${leg}${shouldShowEllipsis(leg) ? '...' : ''}`}</div>
        ))}
      </div>
    );
  }

  const numColumns = legs.some(isNormalLeg) ? 4 : 3;
  return (
    <LegLines data-testid="legs-description-columns" numColumns={numColumns}>
      {displayedLegs.map((leg, index) => {
        const { type, ...legInfo } = getLegInfo(leg);
        const props = {
          showEllipsis: shouldShowEllipsis(leg),
          key: leg,
          index: index + 1,
          ...legInfo,
        };

        switch (type) {
          case 'normal':
            return <NormalLegDescription {...props} />;
          case 'future':
            return <FutureLegDescription {...props} />;
          case 'hedge':
            return <HedgeLegDescription numColumns={numColumns} {...props} />;
          default:
            return null;
        }
      })}
    </LegLines>
  );
}

const MemoizedLegsDescription = memo(
  LegsDescription,
  (prev, next) =>
    isEqual(prev.legs, next.legs) && prev.maxLength === next.maxLength,
);
export default MemoizedLegsDescription;

export const DESCRIPTION_SEPARATOR = '  ';

const isValidForDisplay = (leg: string) => {
  const numberColumns = leg.split(DESCRIPTION_SEPARATOR).length;

  return numberColumns === 3 || numberColumns === 4;
};

const isNormalLeg = (leg: string) =>
  leg.includes('Call') || leg.includes('Put');

const isFutureLeg = (leg: string) => leg.includes('Future');

const getLegInfo = (leg: string) => {
  const legsInfo = leg.split(DESCRIPTION_SEPARATOR);
  if (isNormalLeg(leg)) {
    const [ratio, instrument, expiry, price] = legsInfo;
    return { ratio, instrument, expiry, price, type: 'normal' } as const;
  }

  if (isFutureLeg(leg)) {
    const [ratio, kind, expiry] = legsInfo;
    return { ratio, kind, expiry, type: 'future' } as const;
  }

  const [ratio, expiry, price] = legsInfo;
  return { ratio, expiry, price, type: 'hedge' } as const;
};

interface LegProps {
  readonly expiry?: string;
  readonly ratio?: string;
  readonly instrument?: string;
  readonly kind?: string;
  readonly price?: string;
  readonly index: number;
  readonly numColumns?: number;
  readonly showEllipsis: boolean;
}

function NormalLegDescription({
  expiry,
  ratio,
  instrument,
  index,
  price = '',
  showEllipsis,
}: LegProps) {
  return (
    <>
      <span data-testid={`ratio-${index}`}>{ratio}</span>
      <span data-testid={`instrument-${index}`}>{instrument}</span>
      <span data-testid={`expiry-${index}`}>{expiry}</span>
      <span data-testid={`price-${index}`}>
        {price}
        {showEllipsis ? '...' : ''}
      </span>
    </>
  );
}

function FutureLegDescription({
  ratio,
  kind,
  expiry,
  showEllipsis,
  index,
}: LegProps) {
  return (
    <>
      <span data-testid={`ratio-${index}`}>{ratio}</span>
      <span data-testid={`kind-${index}`}>{kind}</span>
      <span data-testid={`expiry-${index}`}>
        {expiry}
        {showEllipsis ? '...' : ''}
      </span>
    </>
  );
}

function HedgeLegDescription({
  index,
  ratio,
  expiry,
  price,
  numColumns,
  showEllipsis,
}: LegProps) {
  return (
    <>
      <span data-testid={`ratio-${index}`}>{ratio}</span>
      {numColumns === 4 && <span data-testid={`empty-cell-${index}`} />}
      <span data-testid={`expiry-${index}`}>{expiry}</span>
      <span data-testid={`price-${index}`}>
        {price}
        {showEllipsis ? '...' : ''}
      </span>
    </>
  );
}

const LegLines = styled.div<{ numColumns: number }>`
  display: inline-grid;
  grid-template-columns: repeat(${({ numColumns }) => numColumns}, auto);
  column-gap: 4px;
  white-space: nowrap;

  span {
    line-height: 1.2rem;
    font-size: 0.875rem;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    row-gap: 2px;
  }
`;
